import { useCallback, useMemo } from 'react';

import ProfileAvatar from '@/components/Avatar/ProfileAvatar';
import { Flex } from '@/components/base';
import FavoriteToggle from '@/components/Common/FavoriteToggle';
import { ContentTitle } from '@/components/Typography/NamedTypo';
import { CHART_FIELD } from '@/entities/chart/model/constants';
import { useFeedbackTerminology } from '@/entities/workspace';
import { useChartListQuery } from '@/features/(charts)/chart-list/hooks/use-chart-list-query';
import {
  ChartGraphIconMap,
  getChartFieldLabel,
} from '@/features/(charts)/chart-renderer/model/chart-maps';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import service from '@/services/index';
import useUserStore from '@/stores/UserStore';
import { getTimezoneDayjs } from '@/utils/time';

import ChartListItemAction from '../ui/chart-list-item-action';

const VALID_CHART_FIELD_TYPES = new Set<number>(
  Object.values(CHART_FIELD).filter(
    (value): value is number => typeof value === 'number',
  ),
);

const resolveChartListFieldLabel = (fieldType: unknown) => {
  if (typeof fieldType !== 'number' || !VALID_CHART_FIELD_TYPES.has(fieldType)) {
    return 'Unknown data set';
  }

  return getChartFieldLabel(fieldType as CHART_FIELD);
};

const useChartListTable = (selectedTab: StringNumberValue | null) => {
  const user = useUserStore((state) => state.user);
  const { replaceFeedbackTerminology } = useFeedbackTerminology();

  const wr = useWorkspaceRouter();

  const { data, refetch } = useChartListQuery();

  const filteredData = useMemo(() => {
    if (selectedTab === 'starred') return data?.filter((item) => item.starred);
    if (selectedTab === 'my')
      return data?.filter((item) => item.updated_by === user?.user_id);
    if (selectedTab === 'all') return data;
    return [];
  }, [data, selectedTab, user?.user_id]);

  const columns: DataTableColumnT[] = useMemo(() => {
    return [
      {
        columnKey: 'title',
        columnName: 'Title',
        responsive: true,
        visible: true,
        isDefaultColumn: true,
      },
      {
        columnKey: 'last_updated',
        columnName: 'Last updated',
        width: 160,
        visible: true,
        isDefaultColumn: true,
      },
      {
        columnKey: 'updated_by',
        columnName: 'Modified by',
        width: 120,
        visible: true,
        isDefaultColumn: true,
      },
      {
        columnKey: 'field',
        columnName: 'Data set',
        width: 120,
        visible: true,
        isDefaultColumn: true,
      },
      {
        columnKey: 'actions',
        columnName: 'Actions',
        width: 60,
        visible: true,
        isDefaultColumn: true,
      },
    ];
  }, []);

  const toggleFavorite = useCallback(
    async (alias: string, value: boolean) => {
      await service.chart.toggleStarChart(alias, value);
      refetch();
    },
    [refetch],
  );

  const tableData: DataTableRowT[] = useMemo(() => {
    if (!filteredData) return [];

    return (
      filteredData?.map((item) => {
        let field: string | undefined = '';
        field = replaceFeedbackTerminology(
          resolveChartListFieldLabel(item.field?.type),
        );

        const Icon = ChartGraphIconMap.get(item.graph);
        return {
          rowKey: item.alias,
          rowData: [
            {
              columnKey: 'title',
              children: (
                <Flex
                  padding="0px 8px"
                  a="center"
                  g={8}
                  style={{ position: 'relative' }}
                >
                  <Flex style={{ fontSize: 18 }}>{Icon && <Icon />}</Flex>
                  <ContentTitle>{item.name}</ContentTitle>
                  <FavoriteToggle
                    value={item.starred}
                    onChange={(status) => {
                      toggleFavorite(item.alias, status);
                    }}
                  />
                </Flex>
              ),
              onCellClick: () => wr.push(`/charts/${item.alias}`),
              rawData: item.name,
            },
            {
              columnKey: 'last_updated',
              children: getTimezoneDayjs(item.updated_at).fromNow(),
              rawData: item.updated_at,
            },
            {
              columnKey: 'updated_by',
              children: (
                <ProfileAvatar type={1} text={item.updated_by} withText />
              ),
              rawData: item.updated_by,
            },
            {
              columnKey: 'field',
              children: field,
              rawData: field,
            },
            {
              columnKey: 'actions',
              children: (
                <ChartListItemAction alias={item.alias} refetch={refetch} />
              ),
              rawData: 0,
            },
          ],
        };
      }) || []
    );
  }, [filteredData, refetch, replaceFeedbackTerminology, toggleFavorite, wr]);
  return {
    tableData,
    columns,
  };
};

export default useChartListTable;
